import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { inject, observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom-v5-compat';
import { DialogRoute, closeDialog, updateSearch } from 'bernie-core';
import { Layer } from 'bernie-view';
import { PositionType, UitkMenu, UitkMenuContainer, UitkMenuTrigger } from 'uitk-react-menu';
import { UitkDialogContent } from 'uitk-react-dialog';
import { UitkSheet } from 'uitk-react-sheet';
import { UitkToolbar } from 'uitk-react-toolbar';
import { UitkLink } from 'uitk-react-link';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkStepInput } from 'uitk-react-step-input';
import { UitkButtonSize } from 'uitk-react-button';
import { UitkTextInput } from 'uitk-react-input';
import { PrimaryButton as PrimaryButtonEGDS, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import { AgeSelect } from './age-select';
import { InfoMessage } from './info-message';
import { Child, AgeOption, Travelers } from '../types.d';
import { UitkIcon } from 'uitk-react-icons';

// Css Style Import
import(/* webpackChunkName: "cruise-traveler-styles" */ 'src/components/common/modules/form/cruiseTraveller/styles/traveler.css');

export interface ICruiseTraveller {
  setTravelers: Dispatch<SetStateAction<Travelers>>;
}

export const CruiseTraveller: React.FC<any> = inject('context')(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  observer(({ context, setIsTravelersError, showRoom = true, ...props }: any) => {
    const childAgeOptions: AgeOption[] = [];
    childAgeOptions.push({ value: '0', name: 'Under 1' });
    for (let i = 1; i < 18; i++) {
      childAgeOptions.push({ value: i.toString(), name: i.toString() });
    }

    let travelerText = '#notraverlers traveler';
    const travelerTexts = '#notraverlers travelers';

    const [openTravelerMenu, setOpenTravelerMenu] = React.useState(false);
    const [noAdults, setNoAdults] = React.useState(1);
    const [noChild, setNoChild] = React.useState(0);
    let allTravelerCount = Number(noAdults) + Number(noChild);
    const [showErrorSummary, setShowErrorSummary] = React.useState({ hasError: false, infoKey: '' });
    const [travelerChildren, setTravelerChildren] = React.useState<Child[]>([]);
    const [isTravelersValid, setIsTravelersValid] = React.useState(true);

    //----------- Dialog state -------------------

    const location = useLocation();
    const history = useHistory();
    const uniqueDialogId3 = 'dialog-example-3';
    const [dialogShow, setShowDialog] = useState(false);

    const openDialogWithButton = () => {
      const dialogQuery = { pwaDialog: 'dialog-example-3' };

      setShowDialog(true);
      updateSearch({ history, location, newParams: dialogQuery });
    };
    const onDismissFullscreen = () => {
      closeDialog({ history, location });
    };

    //----------- Dialog state -------------------

    const handleClick = (e) => {
      e.preventDefault();
      const previousState = openTravelerMenu;
      setOpenTravelerMenu(!previousState);
    };

    const validateTraveler = (noAdultsCount: number, noChildCount: number, updatedChildCount): void => {
      allTravelerCount = Number(noAdultsCount) + Number(noChildCount);
      setIsTravelersValid(true);
      if (Number(allTravelerCount) > 5) {
        setShowErrorSummary({ hasError: true, infoKey: 'max_traveler' });
        setIsTravelersValid(false);
      } else {
        const checkChildValid = Number(noChildCount) > 0 && updatedChildCount?.filter((a) => a.age === -1)?.length > 0;

        if (checkChildValid) {
          setShowErrorSummary({ hasError: true, infoKey: 'missing_age_of_child' });
          setIsTravelersValid(false);
        } else {
          setShowErrorSummary({ hasError: false, infoKey: 'missing_age_of_child' });
          setIsTravelersValid(true);
        }
      }
    };

    const handleAdultsChange = (e) => {
      const adultsCount = e.target.value;
      setNoAdults(adultsCount);
      validateTraveler(Number(adultsCount), Number(noChild), travelerChildren);
    };

    const handleChildChange = (e) => {
      const changeChildCount = e.target.value;

      // Decrease child
      if (noChild > changeChildCount) {
        const updatedChildCount = [...travelerChildren]?.slice(0, -1);
        setTravelerChildren(updatedChildCount);
        validateTraveler(Number(noAdults), Number(changeChildCount), updatedChildCount);
      }
      // Increase child
      else {
        setTravelerChildren([...travelerChildren, { age: -1 }]);
        let infoMessageKey = 'missing_age_of_child';
        if (Number(changeChildCount) + Number(noAdults) > 5) {
          infoMessageKey = 'max_traveler';
        }
        setShowErrorSummary({ hasError: true, infoKey: infoMessageKey });
        setIsTravelersValid(false);
      }

      setNoChild(changeChildCount);
    };

    const setTravelerText = () => {
      const allTraveler = Number(noAdults) + Number(noChild);
      travelerText = allTraveler > 1 ? travelerTexts : travelerText;
      return travelerText?.replace('#notraverlers', allTraveler.toString());
    };

    const onUpdateChildAge =
      (roomIndex: number, childIndex: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        const age = parseInt(event.target.value, 10);
        const updatedChildAge = travelerChildren?.map((child, j) => {
          if (j === childIndex) {
            return { age };
          }
          return child;
        });

        setTravelerChildren(updatedChildAge);
        const checkEmptyChildAge = updatedChildAge?.filter((a) => a.age === -1);
        let infoMessageKey = '';
        if (Number(noChild) + Number(noAdults) > 5) {
          infoMessageKey = 'max_traveler';
        } else {
          if (checkEmptyChildAge?.length > 0) {
            infoMessageKey = 'missing_age_of_child';
          }
        }

        setShowErrorSummary({
          hasError: checkEmptyChildAge?.length > 0 || Number(noChild) + Number(noAdults) > 5,
          infoKey: infoMessageKey,
        });
      };

    const travelerButtonSubmit = (e) => {
      e.preventDefault();
      if (!showErrorSummary?.hasError) {
        if (isTravelersValid) {
          setOpenTravelerMenu(false);
          if (dialogShow) {
            closeDialog({ history, location });
            setShowDialog(false);
          }
        } else {
          setOpenTravelerMenu(true);
        }
      } else {
        console.log('error: Please fix the errors first');
      }
    };

    const primaryButtonProps: PrimaryButtonProps = {
      text: 'Apply',
      tag: 'a',
      onClickEvent: travelerButtonSubmit,
      gaTracking: {
        eventCategory: 'Button Click',
        eventAction: 'Wizard Traveler',
        eventLabel: `{onSubmit}`,
      },
      exClass: `btn-global btn-global-primary`,
      target: '_blank',
      size: UitkButtonSize.LARGE,
      isFullWidth: true,
    };

    //external Errrohandeling
    useEffect(() => {
      if (setIsTravelersError && setIsTravelersError !== undefined) {
        setIsTravelersError(showErrorSummary?.hasError);
      }
      setIsTravelersValid(!showErrorSummary?.hasError);
    }, [showErrorSummary]);

    const setUpTraveler = (showTravelerText: boolean, type = 'menu') => {
      return (
        <UitkLayoutGrid className={`roomPickerRoom-grid-${type}`} columns={1} space="two">
          <div className={`roomPickerRoom-${type}`} data-testid={`room-1`}>
            {showTravelerText && (
              <>
                <UitkLayoutGridItem>
                  <div className="menu-error">
                    <InfoMessage showInfoMessage={showErrorSummary.hasError} messageKey={showErrorSummary.infoKey} />
                  </div>
                </UitkLayoutGridItem>
              </>
            )}

            <UitkLayoutGridItem>
              <UitkSpacing padding={{ blockstart: 'two' }} margin={{ blockstart: 'two' }}>
                <UitkStepInput
                  decrementText="decrement"
                  description="(18+)"
                  incrementText="increment"
                  max={5}
                  min={1}
                  label={`${noAdults} ${noAdults > 1 ? 'adults' : 'adult'}`}
                  value={noAdults}
                  onChange={handleAdultsChange}
                />
              </UitkSpacing>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem>
              <UitkSpacing padding={{ blockstart: 'two' }} margin={{ blockstart: 'two' }}>
                <UitkStepInput
                  decrementText="decrement"
                  description="0-17"
                  incrementText="increment"
                  max={4}
                  min={0}
                  label={`${noChild} ${noChild == 1 ? 'child' : 'children'}`}
                  value={noChild}
                  onChange={handleChildChange}
                />
              </UitkSpacing>{' '}
            </UitkLayoutGridItem>
            <UitkLayoutGridItem>
              <UitkSpacing padding={{ blockstart: 'two' }} margin={{ blockstart: 'two' }}>
                <>
                  <AgeSelect
                    ageOptions={childAgeOptions}
                    onChange={onUpdateChildAge}
                    children={travelerChildren}
                    padding={{ blockstart: 'one', blockend: 'three' }}
                    menuIndex={props.index}
                    labelKey={`childAgeLabel`}
                    id="child-age-input"
                  />
                </>
              </UitkSpacing>
            </UitkLayoutGridItem>
            {type == 'menu' && (
              <UitkLayoutGridItem>
                <PrimaryButtonEGDS {...primaryButtonProps} />
              </UitkLayoutGridItem>
            )}
          </div>
          {type == 'dialog' && (
            <UitkLayoutGridItem>
              <PrimaryButtonEGDS {...primaryButtonProps} />
            </UitkLayoutGridItem>
          )}
        </UitkLayoutGrid>
      );
    };

    const renderMenu = (): JSX.Element => {
      return (
        <>
          <UitkMenu
            id="openTravelerMenu"
            isOpen={openTravelerMenu}
            onTriggerClick={handleClick}
            disableBodyClickHandler={!!showErrorSummary?.hasError}
          >
            <UitkMenuTrigger>
              <UitkLink>
                <button>
                  <>
                    {setTravelerText()}
                    <UitkIcon name="expand_more" />
                  </>
                </button>
              </UitkLink>
            </UitkMenuTrigger>

            <UitkMenuContainer position={PositionType.RIGHT} autoPosition width={376}>
              {/* <div className="arrow-head"> */}
              <span className="Tooltip__arrow"></span>
              {/* </div> */}
              {setUpTraveler(true, 'menu')}
            </UitkMenuContainer>
          </UitkMenu>
        </>
      );
    };

    const renderDialog = (): JSX.Element => {
      const triggerRef = React.useRef();

      return (
        <>
          <DialogRoute id={uniqueDialogId3}>
            <Layer id="fullscreenDialog" onDismiss={onDismissFullscreen}>
              {/* Fullscreen Dialog has been deprecated. Replace with UITK Full Sheet */}
              <UitkSheet triggerRef={triggerRef} key="Demo" isVisible={true} type="full" returnFocusOnClose={true}>
                <UitkToolbar
                  key="UitkToolbar"
                  navigationContent={{
                    onClick: !showErrorSummary?.hasError ? onDismissFullscreen : () => {},
                    navIconLabel: 'Close the dialog',
                  }}
                  toolbarTitle="Travelers"
                />
                <UitkDialogContent key="UitkDialogContent-1">{setUpTraveler(false, 'dialog')}</UitkDialogContent>
              </UitkSheet>
            </Layer>
          </DialogRoute>
          <UitkLink>
            <UitkTextInput
              icon="people"
              className={`input-box guests`}
              type="text"
              id="guests"
              label=""
              placeholder={`Guests`}
              name="guests"
              value={`${setTravelerText()}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDialogWithButton();
              }}
            />
          </UitkLink>
        </>
      );
    };

    const childrenInputs = () => {
      return travelerChildren.map((child) => {
        return `${child.age}`;
      });
    };

    return (
      <>
        <div>
          <Viewport>
            <ViewSmall>
              <React.Fragment>{renderDialog()}</React.Fragment>
            </ViewSmall>
            <ViewLarge>
              <React.Fragment>{renderMenu()}</React.Fragment>
            </ViewLarge>
          </Viewport>
          <input type="hidden" name="rooms" data-testid="rooms-input" value="1" />
          <input type="hidden" name="traveler-adults" data-testid="adults-input" value={noAdults} />
          <input type="hidden" name="traveler-children" data-testid="children-input" value={childrenInputs()} />
        </div>
      </>
    );
  })
);
